import React, { useEffect, useState } from 'react';
import { Button, Drawer } from '@primitives';
import UploadComponent from '@cp/components/UploadComponent/UploadComponent';
import useUpload from '@cp/components/UploadComponent/useUpload';
import { noop } from '@utils/common';
import UploadSampleFileComponent from '@cp/components/UploadComponent/UploadSampleFileComponent';
import axiosInstance, { ApiKeys, getApiUrl } from '@cp/utils/ApiHelper';
import { AGENCY_CAPPING_DOWNLOAD_TYPE, FILE_UPLOAD_STATUS } from '@cp/pages/AgencyCapping/types';
import { toast } from '@primitives/Toast';
import UploadSuccessComponent from '@cp/components/UploadComponent/UploadSuccessComponent';
import UploadErrorComponent from '@cp/components/UploadComponent/UploadErrorComponent';
import { DownloadIcon } from '@icons';
import { AGENCY_CAPPING_CLICKSTREAM } from '@cp/src/service/clickStream.constant';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';

type UploadDrawerProps = {
  isOpen: boolean;
  setClose: () => void;
};

function UploadDrawer({ isOpen, setClose }: UploadDrawerProps) {
  const { file, setFile, upload, fileStatus, resetFileStatus, error, setError } = useUpload({
    onFileUploadedSuccess: noop
  });

  const [uploadStatus, setUploadStatus] = useState<FILE_UPLOAD_STATUS>(FILE_UPLOAD_STATUS.IDLE);
  const [faliureReport, setFaliureReport] = useState('');
  const [fileReferenceId, setFileReferenceId] = useState('');
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const resetState = () => {
    resetFileStatus();
    setFile(null);
    setUploadStatus(FILE_UPLOAD_STATUS.IDLE);
    setFaliureReport('');
  };
  const handleSampleFileDownload = () => {
    const url = getApiUrl(ApiKeys.AGENCY_CAPPING_SERVICEABILITY_DOWNLOAD);
    axiosInstance
      .get(url, {
        params: { agencyCappingDownloadType: AGENCY_CAPPING_DOWNLOAD_TYPE.SAMPLE_CSV }
      })
      .then(res => {
        window.open(res?.data?.preSignedGetUrl, '_blank');
      })
      .catch(err => {
        toast('Unable to download file please try again', { type: 'error' });
      });
  };

  const pollForValidationStatus = (referenceId: string, timerId: NodeJS.Timer) => {
    const statusUrl = getApiUrl(ApiKeys.AGENCY_CAPPING_SERVICEABILITY_UPLOAD_STATUS, {
      referenceId: referenceId
    });
    axiosInstance.get(statusUrl).then(res => {
      setUploadStatus(res?.data?.status);
      if (res?.data?.errorReportFilePreSignedGetUrl) {
        setFaliureReport(res?.data?.errorReportFilePreSignedGetUrl);
      }

      if (
        res?.data?.status === 'VALIDATION_FAILURE' ||
        res?.data?.status === 'VALIDATION_SUCCESS'
      ) {
        clearInterval(timerId);
      }
    });
  };

  const startUploadingProcess = () => {
    setUploadStatus(FILE_UPLOAD_STATUS.IN_PROGRESS);
    const getUploadUrl = getApiUrl(
      ApiKeys.AGENCY_CAPPING_SERVICEABILITY_UPLOAD,
      {},
      {
        documentType: 'AGENCY_CAPPING',
        documentContentType: 'CSV'
      }
    );
    axiosInstance.get(getUploadUrl).then(res => {
      upload(file, res.data?.preSignedPutUrl)?.then(() => {
        const ackUrl = getApiUrl(ApiKeys.DOCUMENT_UPLOAD_ACK_URL);
        setFileReferenceId(res.data?.referenceId);
        axiosInstance
          .post(ackUrl, {
            referenceId: res.data?.referenceId,
            status: 'UPLOAD_SUCCESS',
            message: ''
          })
          .then(res => {
            //attach poll
            addClickstreamEvent(AGENCY_CAPPING_CLICKSTREAM.LH_SUCCESS_UPLOAD_FILE_UPDATE_CAPPING);
            const timerId: NodeJS.Timer = setInterval(
              () => pollForValidationStatus(res?.data?.referenceId, timerId),
              2000
            );
          });
      });
    });
  };

  useEffect(() => {
    if (file) {
      startUploadingProcess();
    }
  }, [file]);

  const handleSubmit = () => {
    addClickstreamEvent(AGENCY_CAPPING_CLICKSTREAM.LH_CLICK_SUBMIT_BUTTON_UPDATE_CAPPING);
    setIsSubmitLoading(true);
    const submitUrl = getApiUrl(ApiKeys.AGENCY_CAPPING_SUBMIT, { referenceId: fileReferenceId });

    axiosInstance
      .patch(submitUrl)
      .then(res => {
        resetState();
        if (res?.data?.status === 'APPLY_FAILED') {
          addClickstreamEvent(
            AGENCY_CAPPING_CLICKSTREAM.LH_FAILURE_FILE_SUBMISSION_UPDATE_CAPPING,
            {
              faliureReason: 'Error in submitting file'
            }
          );
          throw new Error('Error in submitting file');
        }
        toast('File submitted successfully', { type: 'success' });
        addClickstreamEvent(AGENCY_CAPPING_CLICKSTREAM.LH_SUCCESS_FILE_SUBMISSION_UPDATE_CAPPING);
        setClose();
      })
      .catch(err => {
        toast('Error in submitting file', { type: 'error' });
        addClickstreamEvent(AGENCY_CAPPING_CLICKSTREAM.LH_FAILURE_FILE_SUBMISSION_UPDATE_CAPPING, {
          faliureReason: 'Error in submitting file'
        });
      })
      .finally(() => {
        setIsSubmitLoading(false);
      });
  };
  return (
    <Drawer
      open={isOpen}
      onClose={setClose}
      headerText="Upload Agency capping"
      footer={
        <Button
          disabled={uploadStatus !== FILE_UPLOAD_STATUS?.VALIDATION_SUCCESS || isSubmitLoading}
          variant="primary"
          fullWidth={true}
          onClick={handleSubmit}
        >
          {isSubmitLoading ? 'Submitting' : 'Submit'}
        </Button>
      }
    >
      <div className="text-sm text-[--grayscale-content-2] mt-4">Upload capping file</div>
      {file ? null : (
        <UploadComponent
          setFile={setFile}
          file={file}
          setHasError={setError}
          isSampleFileAvailable={false}
          containerClassName="w-[270px] !m-0 !mt-2"
          isValidationInfoVisible={true}
          supportedFile=".csv"
          MAX_FILE_SIZE_IN_MB={2}
          clickstreamEvent={AGENCY_CAPPING_CLICKSTREAM.LH_ATTEMPT_UPLOAD_FILE_UPDATE_CAPPING}
        />
      )}
      {file && uploadStatus !== FILE_UPLOAD_STATUS.VALIDATION_FAILURE ? (
        <UploadSuccessComponent
          containerClassName="w-[325px] box-border  !px-[16px] !py-[24px] !m-0 !mt-2"
          name={file?.name || ''}
          isInProgress={uploadStatus === FILE_UPLOAD_STATUS.IN_PROGRESS}
          onCancel={() => {
            setFile(null);
            resetFileStatus();
            setUploadStatus(FILE_UPLOAD_STATUS.IDLE);
            setFaliureReport('');
          }}
        />
      ) : null}
      {file && uploadStatus === FILE_UPLOAD_STATUS.VALIDATION_FAILURE ? (
        <UploadErrorComponent
          containerClassName="w-[325px] box-border  !px-[16px] !py-[24px] !m-0 !mt-2"
          message={'error validating fiile'}
          fileName={file?.name || ''}
          showRetryIcon={true}
          onRetry={startUploadingProcess}
          onCancel={() => {
            setFile(null);
            resetFileStatus();
            setUploadStatus(FILE_UPLOAD_STATUS.IDLE);
            setFaliureReport('');
          }}
          showValidationText={false}
        />
      ) : null}
      <div>
        {faliureReport ? (
          <div className="flex justify-between items-center m-2">
            <div className="text-[--red-base]">upload failed</div>
            <div className="flex items-center">
              <Button
                startAdornment={<DownloadIcon color="var(--blue-base)" />}
                variant="text"
                onClick={() => {
                  window.open(faliureReport, '_blank');
                }}
              >
                Download failure report
              </Button>
            </div>
          </div>
        ) : null}
      </div>
      <UploadSampleFileComponent
        description={'State, District, Agency code, Agency, DPD Bucket, Max Cap'}
        heading={'Columns to be included in CSV'}
        onSampleFileDownload={handleSampleFileDownload}
        className="mt-4"
      />
    </Drawer>
  );
}

export default UploadDrawer;
